/**
 * Shared types, ABCI helpers, and constants for the dao/ sub-modules.
 *
 * This module is internal to the dao/ package and provides:
 * - Type definitions (DAOMember, DAOProposal, DAOConfig, etc.)
 * - Low-level ABCI query helpers (queryRender, queryEval)
 * - Username resolution (cache + batch resolve)
 */

import type { AminoMsg } from "../grc20"
import { getUserRegistryPath, networkScopedKey } from "../config"
import { resilientAbciQuery } from "../rpcFallback"

// ── Types ─────────────────────────────────────────────────────

export interface DAOMember {
    address: string
    roles: string[]
    tier: string               // "T1" | "T2" | "T3" | ""
    votingPower: number        // VPPM value (0 if unknown)
    username: string           // @username from profile (empty if unknown)
}

export interface DAOProposal {
    id: number
    title: string
    description: string
    category: string           // proposal category ("governance", "treasury", etc.)
    status: "open" | "passed" | "rejected" | "executed"
    author: string             // @username or address
    authorProfile: string      // profile URL (empty if unknown)
    tiers: string[]            // ["T1","T2","T3"] eligible tiers
    yesPercent: number         // 0-100
    noPercent: number          // 0-100
    yesVotes: number
    noVotes: number
    abstainVotes: number
    totalVoters: number
    proposer: string
    // v2.13: On-chain action metadata (parsed from Render("proposal/{id}"))
    actionType?: string        // basedao Action.Type(), GovDAO executor type
    actionBody?: string        // basedao Action.String(), GovDAO ExecutorString()
    executorRealm?: string     // GovDAO ExecutorCreationRealm()
    // v3.2: Temporal metadata for date display
    createdAtBlock?: number    // Block height at proposal creation (if extractable)
    createdAt?: string         // Wall-clock timestamp ISO string (from tx-indexer, if available)
    // P1-8: set when BOTH vote-detail and vote RPCs failed during enrichment, so the
    // card can show "couldn't load votes" instead of reading as a genuine no-votes proposal.
    enrichFailed?: boolean
    // daokit list rows carry the RESOURCE display name, not the proposal title
    // (the table has no title column). Consumers may replace the title with the
    // detail-page title when set; it also marks rows whose per-voter lists are
    // unknowable (daokit renders aggregate tallies only), which the vote
    // scanners use to avoid phantom "unvoted" claims.
    titleIsPlaceholder?: boolean
}

export interface DAOConfig {
    name: string
    description: string
    threshold: string
    memberCount: number
    memberstorePath: string    // memberstore realm path (empty if N/A)
    tierDistribution: TierInfo[]
    isArchived: boolean        // true if DAO has been archived
}

export interface TierInfo {
    tier: string        // "T1", "T2", "T3"
    memberCount: number
    power: number
}

export interface VoteRecord {
    tier: string        // "T1" | "T2" | "T3"
    vppm: number        // voting power per member
    yesVoters: VoterEntry[]
    noVoters: VoterEntry[]
    abstainVoters: VoterEntry[]
}

export interface VoterEntry {
    username: string
    profileUrl: string
}

// Re-export AminoMsg for builders
export type { AminoMsg }

// ── ABCI Query Helpers ────────────────────────────────────────

/**
 * Query vm/qrender for a realm's Render(path) output.
 * Data format: "pkgpath:renderpath" (colon separator).
 */
export async function queryRender(rpcUrl: string, pkgPath: string, renderPath: string, strict = false): Promise<string | null> {
    return abciQuery(rpcUrl, "vm/qrender", `${pkgPath}:${sanitize(renderPath)}`, strict)
}

/**
 * queryRender for daokit sub-route reads and fallback chains: gnodaokit realms
 * route Render through p/nt/mux, which answers every unknown route with a
 * literal "404" body — a *successful* query that must read as "no such page",
 * or a fallback chain (e.g. getProposalDetail's Render("1") →
 * Render("proposal/1")) short-circuits on the first try and parses "404" as
 * if it were content.
 *
 * NEVER use this for deployment probes: a "404" body is a real answer from a
 * deployed realm, and the Directory's strict root probe (useResolvedDirectoryDaos)
 * treats null as "not deployed here" — nulling "404" there would silently drop
 * live mux-routed realms from the Directory and home cards.
 */
export async function queryRenderPage(rpcUrl: string, pkgPath: string, renderPath: string, strict = false): Promise<string | null> {
    const data = await queryRender(rpcUrl, pkgPath, renderPath, strict)
    if (data !== null && data.trim() === "404") return null
    return data
}

// ── Per-realm dialect memo ────────────────────────────────────
// A realm's render dialect is static per deployment (it changes only when a
// ceremony redeploys the realm), so flavor discovery — which JSON exports
// exist, whether Render("") is a landing page, which detail route answers —
// runs ONCE per realm per session instead of burning guaranteed-dead
// round-trips on every read (a daokit realm answers Render("N") with a mux
// "404" and has no votes route at all).
//
// Two rules keep the memo from ever DEGRADING data:
// 1. Only POSITIVE structural evidence is memoized — "json" (the realm's
//    GetProposalsJSON answered and parsed) or "daokit" (the realm's own
//    landing links its sub-routes). Absence of evidence (a GovDAO-style
//    root listing) is never memoized: a transient probe failure must not
//    freeze a JSON realm onto the markdown path, where vote data parses to
//    zeros. For the same reason "daokit" never OVERWRITES "json".
// 2. A memoized fast path that FAILS forgets the memo (deleteDaoDialect)
//    and falls back to full discovery — so a stale memo after a redeploy
//    self-heals on the next read instead of erroring for the session.
//
// Session-scoped on purpose: invalidateProposalCache does NOT clear it
// (submitting a proposal changes data, not dialect); clearDaoDialects exists
// for tests.

/** How a DAO realm serves its data (only positive findings are memoized). */
export type DaoDialect = "json" | "daokit"

const dialectMemo = new Map<string, DaoDialect>()

const dialectKey = (rpcUrl: string, realmPath: string) => `${rpcUrl}:${realmPath}`

export function getDaoDialect(rpcUrl: string, realmPath: string): DaoDialect | undefined {
    return dialectMemo.get(dialectKey(rpcUrl, realmPath))
}

export function setDaoDialect(rpcUrl: string, realmPath: string, dialect: DaoDialect): void {
    // "json" is the strongest evidence (a working structured endpoint) —
    // never let landing-shape evidence downgrade it (see rule 1 above).
    if (dialect === "daokit" && dialectMemo.get(dialectKey(rpcUrl, realmPath)) === "json") return
    dialectMemo.set(dialectKey(rpcUrl, realmPath), dialect)
}

export function deleteDaoDialect(rpcUrl: string, realmPath: string): void {
    dialectMemo.delete(dialectKey(rpcUrl, realmPath))
}

export function clearDaoDialects(): void {
    dialectMemo.clear()
}

/**
 * Does a realm's landing page link to its OWN daokit sub-route (":proposals",
 * ":members", …)? Anchored to the realm's own link path so a foreign link
 * (e.g. a description mentioning some other realm's ":proposals") can't divert
 * the read to sub-pages the realm doesn't serve.
 */
export function hasOwnSubpageLink(data: string, realmPath: string, base: string): boolean {
    // "gno.land/r/samcrew/memba_dao" renders links as "/r/samcrew/memba_dao:base"
    const linkPath = realmPath.replace(/^[^/]+/, "")
    const esc = `${linkPath}:${base}`.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    return new RegExp(`\\]\\(${esc}\\)`).test(data)
}

/**
 * Detect max page number from a pager footer ("**1** | [2](?page=2) | …").
 * Shared by the GovDAO root pagination and the daokit sub-page/member pagers
 * (avl/pager renders the same "[N](?page=N)" links). Scanning for the MAX is
 * deliberate: it tolerates a leading back-link ("[1](?page=1)") on pages ≥ 2
 * and degrades to harmless over-fetching (empty pages) rather than silent
 * truncation if a page injects a bogus link.
 */
export function detectMaxPage(data: string): number {
    const pageLinks = data.match(/\[(\d+)\]\(\?page=\d+\)/g)
    if (!pageLinks || pageLinks.length === 0) return 1
    let max = 1
    for (const link of pageLinks) {
        const m = link.match(/\[(\d+)\]/)
        if (m) max = Math.max(max, parseInt(m[1], 10))
    }
    return max
}

/**
 * Query vm/qeval for evaluating an expression in a realm.
 */
export async function queryEval(rpcUrl: string, pkgPath: string, expr: string, strict = false): Promise<string | null> {
    return abciQuery(rpcUrl, "vm/qeval", `${pkgPath}.${expr}`, strict)
}

/**
 * Decode a `vm/qeval` string return that carries a JSON payload —
 * `("<go-quoted-json>" string)` — into the parsed value, or null on any failure
 * (so callers can fall back to Render scraping).
 *
 * qeval re-quotes the returned Go string with strconv.Quote-style escaping,
 * which — for our realm encoders that pre-escape control chars to `\uXXXX` —
 * coincides exactly with JSON string escaping. So the correct decode is a
 * DOUBLE JSON.parse: parse the quoted literal to undo the Go-quote, then parse
 * the JSON payload. The old single-pass `.replace(/\\"/g,'"')` corrupted any
 * field carrying a backslash or newline (a title like `Adopt "v2"` round-tripped
 * by luck; `a\b` or a real newline did not — the escapes doubled).
 */
export function parseQevalJSON(raw: string): unknown {
    const m = raw.match(/^\(\s*("[\s\S]*")\s+string\s*\)\s*$/)
    if (!m) return null
    try {
        const payload = JSON.parse(m[1]) // undo the Go-quote → the raw JSON text
        if (typeof payload !== "string") return null
        return JSON.parse(payload) // parse the JSON payload
    } catch {
        return null
    }
}

/**
 * Strip markdown escape backslashes from Render output text.
 * gno#5418 escapes special chars in titles: `\(`, `\)`, `\[`, `\]`, `\_`, `\*`, etc.
 * We strip these so titles display cleanly in the UI.
 */
export function unescapeMarkdown(str: string): string {
    return str.replace(/\\([[\]()_*`~#>+\-.!|{}])/g, "$1")
}

/** Sanitize render path to prevent ABCI query injection.
 *  Allows query params (?key=val&key2=val2) for pagination and filtering. */
export function sanitize(str: string): string {
    return str.replace(/[^a-zA-Z0-9_./:\-?=&]/g, "")
}

/** Low-level ABCI query with automatic RPC failover.
 *  Uses TextDecoder for proper UTF-8 handling (atob alone corrupts multi-byte chars like em dash).
 *  The rpcUrl parameter is kept for API compatibility but the resilient layer
 *  handles failover to backup endpoints automatically. */
async function abciQuery(_rpcUrl: string, path: string, data: string, strict = false): Promise<string | null> {
    return resilientAbciQuery(path, data, strict)
}

// ── Username Resolution ───────────────────────────────────────

/** User registry realm path on gno.land. */
const USER_REGISTRY = getUserRegistryPath()

/** Username cache key in localStorage (W2.2: network-scoped — usernames are
 *  resolved from the ACTIVE chain's r/sys/users; a test12 resolution must not
 *  be served while the app targets test13). Legacy unscoped entries just age
 *  out — the cache has a 1h TTL, no migration needed. */
const USERNAME_CACHE_KEY = networkScopedKey("memba_usernames")

/** Cache TTL: 1 hour (in ms). */
const USERNAME_CACHE_TTL = 60 * 60 * 1000

interface UsernameCache {
    entries: Record<string, { username: string; ts: number }>
}

/** Read username cache from localStorage. */
function readUsernameCache(): UsernameCache {
    try {
        const raw = localStorage.getItem(USERNAME_CACHE_KEY)
        if (!raw) return { entries: {} }
        const parsed = JSON.parse(raw)
        if (typeof parsed === "object" && parsed.entries) return parsed as UsernameCache
    } catch { /* ignore corrupt cache */ }
    return { entries: {} }
}

/** Write username cache to localStorage. */
function writeUsernameCache(cache: UsernameCache): void {
    try {
        localStorage.setItem(USERNAME_CACHE_KEY, JSON.stringify(cache))
    } catch { /* quota exceeded */ }
}

/**
 * Resolve a single g1 address to @username via gno.land user registry.
 * Queries Render(address) which returns: "# User - `username`"
 * Returns "@username" or empty string if not registered.
 */
async function resolveUsername(rpcUrl: string, address: string): Promise<string> {
    try {
        const data = await queryRender(rpcUrl, USER_REGISTRY, address)
        if (!data) return ""
        // Primary format (r/gnoland/users/v1): "# User - `username`"
        // Secondary format (r/sys/users): may differ — try fallback patterns
        const m = data.match(/# User - `([^`]+)`/)
            || data.match(/\*\s+\[([^\]]+)\]\(/)           // " * [username](link)" list format
            || data.match(/username:\s*([a-zA-Z0-9_]+)/)   // structured fallback
        return m ? `@${m[1]}` : ""
    } catch {
        return ""
    }
}

/**
 * Batch-resolve addresses to usernames for a list of members.
 * Uses localStorage cache with 1-hour TTL:
 * - Cache hit (fresh): use cached username instantly, no ABCI call
 * - Cache miss or stale: resolve via ABCI, update cache
 * Resolves cache misses in parallel for speed.
 */
export async function resolveUsernames(rpcUrl: string, members: DAOMember[]): Promise<void> {
    const cache = readUsernameCache()
    const now = Date.now()
    const toResolve: number[] = [] // indices of members needing ABCI resolution

    // Phase 1: populate from cache, identify misses
    for (let i = 0; i < members.length; i++) {
        const entry = cache.entries[members[i].address]
        if (entry && (now - entry.ts) < USERNAME_CACHE_TTL) {
            // Cache hit — use cached username
            members[i].username = entry.username
        } else {
            toResolve.push(i)
        }
    }

    // Phase 2: resolve cache misses in parallel
    if (toResolve.length > 0) {
        const results = await Promise.all(
            toResolve.map((idx) => resolveUsername(rpcUrl, members[idx].address)),
        )
        results.forEach((username, j) => {
            const idx = toResolve[j]
            members[idx].username = username
            cache.entries[members[idx].address] = { username, ts: now }
        })
        writeUsernameCache(cache)
    }
}

/** Normalize status string from various dao formats. */
export function normalizeStatus(s: string): DAOProposal["status"] {
    const lower = s.toLowerCase()
    if (lower.includes("accept") || lower.includes("pass")) return "passed"
    // "deni"/"deny" covers GovDAO v3's detail-render prose "PROPOSAL HAS BEEN DENIED"
    // NOTE: daokit's detail-page "Closed" is mapped inside the daokit leg of
    // getProposalDetail, NOT here — this funnel is shared across dialects, and
    // other callers may pass unknown vocabulary ("disclosed", "voting closed").
    // Keep their warning → "open" behavior rather than silently treating any
    // occurrence of "closed" as a rejected proposal.
    if (lower.includes("reject") || lower.includes("fail") || lower.includes("deni") || lower.includes("deny")) return "rejected"
    if (lower.includes("exec") || lower.includes("complete")) return "executed"
    if (lower.includes("active") || lower.includes("open") || lower === "") return "open"
    console.warn(`[normalizeStatus] Unknown proposal status: "${s}" — defaulting to "open"`)
    return "open"
}

/** Status badge colors and labels — single source of truth for ProposalCard + ProposalView. */
export const PROPOSAL_STATUS_COLORS: Record<string, { bg: string; color: string; label: string }> = {
    open: { bg: "rgba(0,212,170,0.08)", color: "#00d4aa", label: "ACTIVE" },
    passed: { bg: "rgba(76,175,80,0.08)", color: "#4caf50", label: "PASSED" },
    rejected: { bg: "rgba(244,67,54,0.08)", color: "#f44336", label: "REJECTED" },
    executed: { bg: "rgba(33,150,243,0.08)", color: "#2196f3", label: "EXECUTED" },
}
